import lod_ from "lodash";
import { List, ListItemButton, CircularProgress, Icon, IconButton, Tooltip } from "@mui/material";
import SignalCellular0BarIcon from "@mui/icons-material/SignalCellular0Bar";
import SignalCellular1BarIcon from "@mui/icons-material/SignalCellular1Bar";
import SignalCellular2BarIcon from "@mui/icons-material/SignalCellular2Bar";
import SignalCellular3BarIcon from "@mui/icons-material/SignalCellular3Bar";

import SignalCellularConnectedNoInternet4BarIcon from "@mui/icons-material/SignalCellularConnectedNoInternet4Bar";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import Message from "./Message";
import { t } from "i18next";
import { display } from "redux-react/reducers/snackBarReducer";
import { useDispatch } from "react-redux";

import ResourceActions from "redux-react/actions/resourceActions";
import { useEffect, useState } from "react";

const toCOC = process.env.REACT_APP_COC;

/**
 * Formats a timestamp into a cockpit-readable date (fr-FR).
 * @param {number|string} timestamp - Timestamp to format.
 * @returns {string} Formatted date or empty string if invalid.
 */
const formatCockpitDate = timestamp => {
	if (lod_.isNil(timestamp)) {
		return "";
	}

	const numericTimestamp = Number(timestamp);
	if (Number.isNaN(numericTimestamp)) {
		return "";
	}

	const tsInMs = numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp;
	const date = new Date(tsInMs);

	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return date.toLocaleString("fr-FR");
};

/**
 * Returns the priority icon for a conversation.
 * @param {number} priority - Priority level (1 to 5).
 * @returns {JSX.Element} Priority icon or empty element.
 */
const getPriorityInfo = priority => {
	switch (priority) {
		case 1:
			return <SignalCellular0BarIcon fontSize="small" sx={{ color: "text.disabled" }} />;

		case 2:
			return <SignalCellular1BarIcon fontSize="small" sx={{ color: "text.secondary" }} />;

		case 3:
			return <SignalCellular2BarIcon fontSize="small" sx={{ color: "info.main" }} />;

		case 4:
			return <SignalCellular3BarIcon fontSize="small" sx={{ color: "warning.main" }} />;

		case 5:
			return (
				<SignalCellularConnectedNoInternet4BarIcon fontSize="small" sx={{ color: "error.main" }} />
			);

		default:
			return <></>;
	}
};
/**
 * Displays the conversation list and the selected conversation messages in a split view.
 * @param {Array} conversations - The list of conversation objects.
 * @returns {JSX.Element} The rendered component.
 */
const ConversationsTab = ({ conversations, resourceCode }) => {
	const dispatch = useDispatch();
	const [conversationMessagesByFid, setConversationMessagesByFid] = useState({});
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [selectedConversationFid, setSelectedConversationFid] = useState(null);

	/**
	 * Fetches messages for a conversation.
	 * @param {string} fID - Conversation identifier.
	 */
	const getMessagesConversation = async fID => {
		if (lod_.isEmpty(fID)) {
			return;
		}

		if (!lod_.isNil(conversationMessagesByFid[fID])) {
			return;
		}

		setLoadingMessages(true);

		const onSuccess = res => {
			const messages =
				Array.isArray(res?.[0]?.messages) && !lod_.isNil(res?.[0]?.messages) ? res[0].messages : [];

			setConversationMessagesByFid(prevState => ({
				...prevState,
				[fID]: messages
			}));

			setLoadingMessages(false);
		};

		const onFailure = () => {
			setConversationMessagesByFid(prevState => ({
				...prevState,
				[fID]: []
			}));
			setLoadingMessages(false);
		};

		dispatch(
			ResourceActions.getResources(
				resourceCode,
				{
					filter: `header.fID="${fID}",messageRequested=true`
				},
				onSuccess,
				onFailure
			)
		);
	};

	const handleSelectConversation = conversation => {
		const fid = conversation?.header?.fID;
		setSelectedConversationFid(fid);
		getMessagesConversation(fid);
	};

	useEffect(() => {
		if (lod_.isEmpty(conversations)) {
			setSelectedConversationFid(null);
			return;
		}

		const firstFid = conversations?.[0]?.header?.fID;
		if (selectedConversationFid !== firstFid) {
			setSelectedConversationFid(firstFid);
		}

		getMessagesConversation(firstFid);
	}, [conversations]);

	if (lod_.isNil(conversations) || lod_.isEmpty(conversations)) {
		return (
			<MDBox p={2} textAlign="center">
				{t("FORMS.RESOURCE.listConversations.noConversation")}
			</MDBox>
		);
	}

	const selectedConversation = conversations.find(
		conversation => conversation?.header?.fID === selectedConversationFid
	);
	const selectedConversationMessages = !lod_.isNil(
		conversationMessagesByFid[selectedConversationFid]
	)
		? conversationMessagesByFid[selectedConversationFid]
		: selectedConversation?.messages || [];

	return (
		<MDBox mt={1} display="flex" gap={2} sx={{ height: "100%" }}>
			<MDBox
				sx={{
					width: "35%",
					minWidth: 280,
					borderRight: theme => `1px solid ${theme.palette.divider}`,
					overflowY: "auto",
					pr: 1
				}}
			>
				<List dense sx={{ p: 0 }}>
					{conversations.map((conversation, index) => {
						const cockpitTitle = conversation?.meta?.cockpit?.title;
						const cockpitDate = formatCockpitDate(conversation?.meta?.cockpit?.ts);
						const fid = conversation?.header?.fID;
						const priority = getPriorityInfo(conversation?.meta?.priority);
						const hasNote =
							conversation?.meta?.cockpit?.hasNote === true ||
							conversation?.meta?.cockpit?.hasContactNote === true;
						const isSelected = selectedConversationFid === fid;

						return (
							<ListItemButton
								key={conversation?.fID ?? index}
								onClick={() => handleSelectConversation(conversation)}
								sx={{
									mb: 1,
									borderRadius: "8px",
									alignItems: "flex-start",
									border: theme =>
										`1px solid ${isSelected ? theme.palette.info.main : theme.palette.divider}`
								}}
							>
								<MDBox
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									width="100%"
								>
									<MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
										<Icon fontSize="small" color="info">
											chat
										</Icon>
										<MDBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
											<MDTypography variant="button" fontWeight="medium" noWrap>
												{!lod_.isEmpty(cockpitTitle) ? cockpitTitle : `${fid}`}
											</MDTypography>
											<MDBox display="flex" flexDirection="row" sx={{ minWidth: 0 }}>
												{!lod_.isEmpty(fid) && (
													<MDTypography
														variant="caption"
														pr={1}
														color="text"
														sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
														onClick={e => {
															e.stopPropagation();
															navigator.clipboard.writeText(fid);
															dispatch(
																display({
																	message: t("FORMS.copyToClipboard"),
																	type: "info"
																})
															);
														}}
													>
														{fid}
													</MDTypography>
												)}

												{!lod_.isEmpty(cockpitDate) && (
													<MDTypography variant="caption" color="text" noWrap>
														{cockpitDate}
													</MDTypography>
												)}
											</MDBox>
										</MDBox>
									</MDBox>

									<MDBox display="flex" alignItems="center" gap={0.5} pl={1}>
										<MDBox display="flex" alignItems="center">
											{priority}
										</MDBox>

										{hasNote && (
											<Tooltip
												title={t("FORMS.RESOURCE.listConversations.hasNote")}
												placement="top"
											>
												<Icon fontSize="small" color="warning">
													sticky_note_2
												</Icon>
											</Tooltip>
										)}

										<Tooltip
											title={t("FORMS.RESOURCE.listConversations.openInCOC")}
											placement="top"
										>
											<IconButton
												size="small"
												color="info"
												onClick={e => {
													e.stopPropagation();
													window.open(
														`${toCOC}/admin/cockpit?conversation=${conversation?.header?.fID}`,
														"_blank"
													);
												}}
											>
												<Icon fontSize="small">open_in_new</Icon>
											</IconButton>
										</Tooltip>
									</MDBox>
								</MDBox>
							</ListItemButton>
						);
					})}
				</List>
			</MDBox>

			<MDBox sx={{ flex: 1, overflowY: "auto" }}>
				{loadingMessages ? (
					<MDBox
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={{ height: "100%", minHeight: 200 }}
					>
						<CircularProgress color="info" size={40} />
					</MDBox>
				) : !lod_.isNil(selectedConversationMessages) &&
				  !lod_.isEmpty(selectedConversationMessages) ? (
					<MDBox
						borderRadius="md"
						p={1}
						style={{
							backgroundColor: "rgb(236 236 236)",
							overflowY: "auto",
							height: "100%"
						}}
					>
						{selectedConversationMessages.map((message, msgIndex) => (
							<Message key={msgIndex} message={message} />
						))}
					</MDBox>
				) : (
					<MDBox p={2} textAlign="center">
						<MDTypography variant="caption" color="text">
							{t("FORMS.RESOURCE.listConversations.noConversationMessages")}
						</MDTypography>
					</MDBox>
				)}
			</MDBox>
		</MDBox>
	);
};

export default ConversationsTab;
